// SelectFilterVirtualized.jsx
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  Tooltip,
  Divider,
  Stack,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useState, useRef, useEffect, Fragment, memo } from "react";
import { FixedSizeList as List } from "react-window";

import CloseIcon from "@mui/icons-material/CloseRounded";
import { ArrowDropDown } from "@mui/icons-material";
import useStyles from "./styles";

function Tag({ label, onDelete }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <Tooltip title={label} placement="top">
      <Box sx={styles.divTag(!!onDelete)}>
        <Typography sx={styles.textTag}>{label}</Typography>
        {onDelete && <CloseIcon sx={styles.closeIcon} onClick={onDelete} />}
      </Box>
    </Tooltip>
  );
}

// ✅ Item virtualizado (apenas renderiza os visíveis)
const Row = memo(function Row({
  index,
  style,
  data: {
    options,
    value,
    onChange,
    disabled,
    styles,
    setFocused,
    focused,
    label,
  },
}) {
  const option = options[index];
  const isSelected = value.includes(option);

  return (
    <div style={style}>
      <Tooltip title={option}>
        <MenuItem
          key={`option-${index}`}
          sx={{
            ...styles.menuItem(isSelected, false),
            "&.Mui-disabled": { filter: "opacity(1)" },
          }}
          onMouseEnter={() => setFocused(option)}
          onMouseLeave={() => setFocused("")}
          onClick={() => {
            const newSelectedValue = isSelected
              ? value.filter((item) => item !== option)
              : [...value, option];
            onChange(newSelectedValue);
          }}
          disabled={disabled}
        >
          <Checkbox
            checked={isSelected}
            size="small"
            color="primary"
            disableRipple
            sx={{ m: -1, "&:hover": { bgcolor: "transparent" } }}
          />
          <Typography
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              width: "fit-content",
              maxWidth: "100%",
              textOverflow: "ellipsis",
            }}
          >
            {option ?? "Sem " + label?.toLowerCase()}
          </Typography>
        </MenuItem>
      </Tooltip>
    </div>
  );
});

const SelectFilter = ({
  label = "",
  options = [],
  value = [],
  onChange = () => {},
  onClose = () => {},
  disabled = false,
  renderItens = 1,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const styles = useStyles(theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const [focused, setFocused] = useState("");
  const [listBoxWidth, setListBoxWidth] = useState(0);
  const open = Boolean(anchorEl);

  // Remove duplicatas
  const optionsGrouped = options.reduce((acc, option) => {
    if (!acc.includes(option)) acc.push(option);
    return acc;
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      setListBoxWidth(inputRef.current.offsetWidth);
    }
  }, [value]);

  const isAllSelected = value.length === optionsGrouped.length;

  return (
    <Fragment>
      {/* Input principal */}
      <Box sx={styles.boxContainer}>
        <Box
          ref={inputRef}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            event.stopPropagation();
          }}
          sx={styles.boxInputSelect}
        >
          <Typography sx={styles.label}>{label.toUpperCase()}</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Box sx={styles.boxTags}>
              {isAllSelected ? (
                <Tag label="todos" />
              ) : (
                <>
                  {(open ? value.slice(0, renderItens) : value.slice(0, 1)).map(
                    (option, index) => (
                      <Tag
                        key={option + index}
                        label={option ?? "Sem " + label?.toLowerCase()}
                        onDelete={(event) => {
                          event.stopPropagation();
                          const newSelectedValue = value.filter(
                            (item) => item !== option
                          );
                          onChange(newSelectedValue);
                        }}
                      />
                    )
                  )}
                </>
              )}
            </Box>
            <ArrowDropDown
              sx={{ fontSize: 20, color: "text.secondary", marginLeft: "auto" }}
            />
          </Box>
        </Box>
      </Box>

      {/* Menu de opções */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          onClose?.();
          setAnchorEl(null);
        }}
        sx={styles.menu(listBoxWidth)}
        autoFocus={false}
        disableAutoFocusItem
        disableEnforceFocus
      >
        <MenuItem
          key="select-all"
          sx={styles.menuItem(isAllSelected, false)}
          onMouseEnter={() => setFocused("selectAll")}
          onMouseLeave={() => setFocused("")}
          onClick={() => {
            onChange(isAllSelected ? [] : optionsGrouped);
          }}
          disabled={disabled}
        >
          <Checkbox
            checked={isAllSelected}
            size="small"
            color="primary"
            disableTouchRipple
            sx={{ m: -1, "&:hover": { bgcolor: "transparent" } }}
          />
          Selecionar tudo
        </MenuItem>

        <Divider />

        {/* Lista virtualizada */}
        <List
          height={200} // altura visível
          width={"100%"} // usa 100% da largura do Menu
          itemCount={optionsGrouped?.length}
          itemSize={38} // altura de cada item
          itemData={{
            options: optionsGrouped,
            value,
            onChange,
            disabled,
            styles,
            setFocused,
            focused,
            label,
          }}
          style={{
            overflowX: "hidden", // remove a barra horizontal
          }}
        >
          {Row}
        </List>
      </Menu>
    </Fragment>
  );
};

export default SelectFilter;
